/**
 * F24 (#3991) — is this a signed-in user booting on a device that has never synced, while offline?
 *
 * The situation this catches: someone who already has an account signs in on a NEW device (or a
 * fresh browser profile) with no connection. All of their data exists — on the server. But this
 * device has never synced, so there is nothing locally, and the app cheerfully routes them into
 * empty onboarding exactly as if they were brand new. That is a lie, and a frightening one: it
 * looks like their account and their whole history is gone.
 *
 * When this returns true, the boot should NOT route to onboarding. It should say plainly that the
 * device is offline and their account will load once there is a connection.
 *
 * The test is deliberately narrow, because being wrong the other way — holding a genuinely new
 * user on a splash screen forever — is just as bad. That is not hypothetical: the first version of
 * this gate asked "has this device recently synced?", and the answer for someone who signed up on
 * this device is NO, because a signup syncs against an empty remote and that case deliberately
 * withholds the recently-synced marker. Such a user, if they quit before finishing onboarding and
 * reopened the app offline, was held on the splash forever and told to connect so we could load an
 * account that does not exist on the server. Exactly the kind of lie this whole change exists to
 * remove, reintroduced by the fix for it.
 *
 * So the question is now the weaker and more honest one: has this device ever FINISHED an initial
 * sync for this account (empty remote included)? If it has, there is nothing on the server we have
 * not already seen, so an empty local database means the user really is new — let them onboard.
 */
export function isNeverSyncedOfflineBoot(input: {
  /** Has the first sync completed in THIS session? undefined = still running, false = it failed. */
  initialSyncCompleted: boolean | undefined;
  /** Has this local database ever FINISHED an initial sync on this device, in ANY session?
   *  (localStorage marker; true even when the remote turned out to be empty.) This is what tells
   *  "offline on a device I have used before" — where the app must boot normally — apart from
   *  "offline on a device that has never once reached this account's data". */
  hasSyncedOnThisDeviceMarker: boolean;
  /** Is the device online right now? */
  connectionOnline: boolean;
}): boolean {
  return input.initialSyncCompleted !== true
    && !input.hasSyncedOnThisDeviceMarker
    && !input.connectionOnline;
}

/**
 * #4617 — the same never-synced device, but the network is UP and our backend is not answering.
 *
 * `isNeverSyncedOfflineBoot` above only catches the boot where the DEVICE decides it is offline.
 * There is a second way to reach the identical dead end: the network works, the couch login to our
 * backend fails anyway, that login is deferred to a background retry, and the boot carries on into
 * new-user onboarding. To someone who already has an account, that screen looks exactly as if their
 * whole history were gone — the very lie the gate above exists to remove.
 *
 * THIS FUNCTION CHANGES NOTHING. It only names the situation so the boot can emit a silent problem
 * for it. Whether the gate should widen to hold the splash here too was a product question,
 * decided on #4617 (2026-10-02: do not widen; 0 production events), and it is a real trade
 * rather than an obvious improvement: holding also requires that no plan document exists locally,
 * and someone who has just CREATED an account has no plan document either (Firebase sign-up
 * works while our backend is down). Widening would therefore strand a genuinely new user on the
 * splash — the same regression the gate above was narrowed to fix. Before making that trade we
 * wanted to know how often this happens, and nothing else the app records separates a never-synced
 * device from any other. Hence a marker rather than a decision, kept so a future occurrence still
 * reports (design-docs/telemetry.md says what to do then).
 */
export function isNeverSyncedServerUnreachableBoot(input: {
  /** Has the first sync completed in THIS session? undefined = still running, false = it failed. */
  initialSyncCompleted: boolean | undefined;
  /** Has this local database ever FINISHED an initial sync on this device, in ANY session? */
  hasSyncedOnThisDeviceMarker: boolean;
  /** Is the device online right now? Here it must be TRUE — that is what separates this reading
   *  from the gate above, so one boot can never satisfy both. */
  connectionOnline: boolean;
  /** Does this device hold NO plan document at all? The caller tests `_rev`, not any field value —
   *  see the gate's own comment in app.component.ts for why a default-valued plan object looks
   *  identical to a real one. */
  localPlanIsEmpty: boolean;
  /** Did the couch login fail and get deferred to a background retry? This is what says the backend
   *  did not answer, as opposed to a boot that simply has not tried yet. */
  couchLoginRetryNeeded: boolean;
}): boolean {
  return input.initialSyncCompleted !== true
    && !input.hasSyncedOnThisDeviceMarker
    && input.connectionOnline
    && input.localPlanIsEmpty
    && input.couchLoginRetryNeeded;
}
